import {
  GrpcResponseErrorDetail,
  useLegacyGrpcRequestCallback,
} from '@aboutyou/data-fetcher';
import {CheckoutV1_logInWithFacebookLazy} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.grpc.lazy';
import {CustomerGender} from '@aboutyou/grpc-clients/grips/protos/shared/shared.types';
import {useTranslation} from '@aboutyou/i18n';
import {AuthenticationEvent, Log} from '@aboutyou/mlm';
import {useCompleteLocation} from '@aboutyou/router';
import {
  useIsBasketStateLoaded,
  useIsWishlistLoaded,
  useUserGender,
} from '@aboutyou/user';
import {useCallback, useEffect, useRef} from 'react';
import {SSOParams} from '../../hooks/constants';
import {useAuthLogger} from '../../hooks/useAuthLogger';
import {
  SkipErrorLogging,
  getAuthErrorMetadata,
  useAuthResponseHandler,
} from '../../hooks/useAuthResponseHandler';
import {
  AuthMethod,
  FacebookFailureResponse,
  FacebookLoginInfo,
} from '../../types';

const FB_SCOPE = {
  Profile: 'public_profile',
  Email: 'email',
};

export const useFacebookLoginHandler = (
  method: AuthMethod,
  {
    onError,
    onSuccess,
    disableSuccessToast,
  }: {
    onError: (error: string) => void;
    onSuccess: () => void;
    disableSuccessToast: boolean;
  },
) => {
  const {Log: AuthLog, resetLoggerInstanceId} = useAuthLogger();
  const isBasketStateLoaded = useIsBasketStateLoaded();
  const isWishlistLoaded = useIsWishlistLoaded();

  const location = useCompleteLocation();
  const search = location.search;
  const isFacebookPopupStuck =
    location.hash?.match('#cb=') &&
    location.hash?.includes('graph_domain=facebook');

  const locationHashParams = new URLSearchParams(
    location.hash.replace('#', '?'),
  );
  const accessTokenFromHash = locationHashParams.get(SSOParams.AccessToken);
  const signedRequestFromHash = locationHashParams.get(SSOParams.SignedRequest);
  const grantedScopesFromHash = locationHashParams.get(SSOParams.GrantedScopes);
  const expiresInFromHash = locationHashParams.get(SSOParams.ExpiresIn);

  const isRedirectFromFacebookLegacy =
    isFacebookPopupStuck && signedRequestFromHash;

  const {handleAuthResponse, loading, setLoading} = useAuthResponseHandler({
    disableSuccessToast,
  });
  const gender = useUserGender();
  const {t} = useTranslation();
  const logInWithFacebook = useLegacyGrpcRequestCallback(
    CheckoutV1_logInWithFacebookLazy,
  );

  const sdkErrorLogged = useRef(false);

  const onClick = useCallback(() => {
    AuthLog({
      event: 'sso-open-url',
      state: null,
      attributes: {
        idp: 'legacy-facebook',
        idp_key: 'legacy-facebook',
      },
    });
    setLoading(true);
  }, [AuthLog, setLoading]);

  const submitHandler = useCallback(
    (data: FacebookLoginInfo | FacebookFailureResponse) => {
      AuthLog({
        event: 'sso-redirect',
        state: null,
        attributes: {
          idp: 'legacy-facebook',
          idp_key: 'legacy-facebook',
        },
      });

      if (isFbLoginSuccess(data)) {
        logInWithFacebook
          .mutateAsync({
            gender:
              gender === 'male' ? CustomerGender.MALE : CustomerGender.FEMALE,
            access: {
              token: {
                token: data.accessToken,
                userId: data.userID,
                expiresAt: new Date(data.data_access_expiration_time * 1000),
                scopes: [FB_SCOPE.Profile, FB_SCOPE.Email],
              },
              $case: 'token',
            },
          })
          .then(data => {
            AuthLog({
              event: 'sso-success',
              state: null,
              attributes: {
                idp: 'legacy-facebook',
                idp_key: 'legacy-facebook',
              },
            });

            return handleAuthResponse({method, medium: 'facebook'})(data);
          })
          .then(onSuccess)
          .catch(error => {
            if (error instanceof SkipErrorLogging) return;

            const missingPermission =
              getGrpcFieldViolationsAsFormError(error.details, ['NoEmail'])
                .length === 1;
            if (missingPermission) {
              onError(
                t(
                  'To register with facebook you need an account with a linked email address. Please leave an email address or use the registration form below.',
                ),
              );
            } else {
              onError(t('Uh oh, something went wrong.'));
            }
            const metadata = getAuthErrorMetadata(error);

            Log.compare({
              actionName: AuthenticationEvent,
              success: false,
              attributes: {
                method,
                medium: 'facebook',
                code: metadata.code,
                reason: metadata.message,
                metadata,
              },
            });

            AuthLog({
              event: 'sso-fe-error',
              state: null,
              attributes: {
                idp: 'legacy-facebook',
                idp_key: 'legacy-facebook',
                reason: missingPermission
                  ? 'missing-permission'
                  : 'login-with-code',
                error_detail: `${metadata.message}:${metadata.code}`,
                metadata,
              },
            });
          })
          .finally(() => {
            resetLoggerInstanceId();
            setLoading(false);
          });
      } else {
        setLoading(false);
        let missingPermission = false;

        if (
          'grantedScopes' in data &&
          typeof data.grantedScopes === 'string' &&
          !data.grantedScopes.includes('email')
        ) {
          missingPermission = true;
          onError(
            t(
              'We are missing some approvals for your profile. Please allow the app permissions',
            ),
          );
        } else {
          onError(t('Uh oh, something went wrong.'));

          // the "unknown" status happens when the user cancels the login
          // we don't want to log this as a login failure
          if (data.status === 'unknown') return;

          // sans the above two cases, we are only aware of errors where the SDK loading itself failed for some reason.
          // In that case we don't want to log the error multiple times if the user rage-clicks the button.
          if (sdkErrorLogged.current) return;

          const metadata = getAuthErrorMetadata(data);

          Log.compare({
            actionName: AuthenticationEvent,
            success: false,
            attributes: {
              method,
              medium: 'facebook',
              code: metadata.code,
              reason: metadata.message,
              metadata,
            },
          });
          AuthLog({
            event: 'sso-fe-error',
            state: null,
            attributes: {
              idp: 'legacy-facebook',
              idp_key: 'legacy-facebook',
              reason: missingPermission ? 'missing-permission' : 'unknown',
              error_detail: `${metadata.message}:${metadata.code}`,
              metadata,
            },
          });

          sdkErrorLogged.current = true;
        }
      }
    },
    [handleAuthResponse, loading, method, setLoading, AuthLog],
  );

  /**
   * Handle redirect from Facebook legacy login.
   * Facebook redirects the user back to the app with a signed_request in the URL hash, if the popup gets stuck.
   */
  useEffect(() => {
    if (
      signedRequestFromHash &&
      !loading &&
      isRedirectFromFacebookLegacy &&
      isBasketStateLoaded &&
      isWishlistLoaded
    ) {
      const attributes = getAttributesFromSignedRequest(signedRequestFromHash);
      if (!attributes) {
        // eslint-disable-next-line local-rules/set-state-in-use-effect
        setLoading(false);
        return;
      }
      const accessData: FacebookLoginInfo = {
        userID: attributes?.user_id || '',
        accessToken: accessTokenFromHash || '',
        data_access_expiration_time: expiresInFromHash
          ? parseInt(expiresInFromHash, 10)
          : 0,
        grantedScopes: grantedScopesFromHash || '',
      };

      setLoading(true);
      AuthLog({
        event: 'sso-open-url',
        state: null,
        attributes: {
          idp: 'legacy-facebook',
          idp_key: 'legacy-facebook',
          redirected: true,
        },
      });

      submitHandler(accessData);
    }
  }, [
    search,
    loading,
    accessTokenFromHash,
    signedRequestFromHash,
    isBasketStateLoaded,
    isWishlistLoaded,
    isRedirectFromFacebookLegacy,
    expiresInFromHash,
    grantedScopesFromHash,
    setLoading,
    AuthLog,
  ]);

  return {submitHandler, loading, onClick};
};

const isFbLoginSuccess = (
  data: FacebookLoginInfo | FacebookFailureResponse,
): data is FacebookLoginInfo => {
  return 'accessToken' in data && data.grantedScopes?.includes(FB_SCOPE.Email);
};

function getErrorTypes(detail: GrpcResponseErrorDetail) {
  switch (detail.type) {
    case 'bad-request':
      return detail.fieldViolations.map(({field}) => field);
    case 'precondition-failure':
      return detail.violations.map(({type}) => type);
  }

  return [];
}

export function getGrpcFieldViolationsAsFormError<T extends string[]>(
  errorDetails: GrpcResponseErrorDetail[],
  fieldsToCheck: T,
) {
  return (errorDetails || [])
    .map(getErrorTypes)
    .flat()
    .filter(type => fieldsToCheck.includes(type));
}

const getAttributesFromSignedRequest = (signedRequest: string) => {
  const [_, encodedPayload] = signedRequest.split('.');
  if (!encodedPayload) {
    return null;
  }

  try {
    const decodedState = JSON.parse(atob(encodedPayload)) as {
      user_id?: string;
    };

    return decodedState;
  } catch (error) {
    return null;
  }
};
